import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import * as SentryUtils from "../utils/sentry";
import { Button } from "../components/common/Button";
import {
  ErrorMessage,
  FormContainer,
  FormGroup,
  FormHeading,
  Input,
  Label,
} from "../components/common/Form";

import { BackButton } from "../components/common/BackButton";
import { PageTitle } from "../components/common/PageData";
import { Member, useUserStore } from "../stores/user.store";
import { DateValidation } from "../utils/dateValidation";

type FormInputs = {
  firstName: string;
  lastName: string;
  birthday: string;
};

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
`;

export const EditMember = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNewMember = id === "new";
  const [member, setMember] = useState<Member | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { profile, addMember, updateMember, deleteMember } = useUserStore();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>();

  useEffect(() => {
    const profile = useUserStore.getState().profile;
    if (profile) {
      const member = profile.members.find((m) => m.id === id);
      if (member) {
        setMember(member);

        reset({
          firstName: member.first_name,
          lastName: member.last_name,
          birthday: member.date_of_birth,
        });
      }
    }
  }, [id, isNewMember, reset, setMember]);

  const onSubmit = async (data: FormInputs) => {
    if (profile === null) {
      console.error("Setting a user member when profile is not present");
      return;
    }
    setIsSaving(true);
    try {
      if (member === null) {
        await addMember({
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.birthday,
          profile_id: profile.id,
        });
      } else {
        await updateMember({
          id: member.id,
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.birthday,
          profile_id: profile.id,
        });
      }

      navigate("/account/members");
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: {
          component: "EditMember",
          operation: member === null ? "add_member" : "update_member",
        },
        extra: { memberId: member?.id, profileId: profile.id },
      });
      console.error("Error saving member:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (member === null) {
      console.error("Trying to delete a member that does not exist");
      return;
    }

    if (
      window.confirm(
        "Sei sicuro di voler eliminare questo componente familiare?",
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteMember(member.id);
        navigate("/account/members");
      } catch (error) {
        SentryUtils.captureException(error, {
          tags: { component: "EditMember", operation: "delete_member" },
          extra: { memberId: member.id },
        });
        console.error("Error deleting member:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <FormContainer>
      <FormHeading>
        <BackButton onClick={() => navigate("/account/members")} />
        <PageTitle>
          {isNewMember ? "AGGIUNGI COMPONENTE" : "MODIFICA DATI COMPONENTE"}
        </PageTitle>
      </FormHeading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="first_name">Nome</Label>
          <Input
            id="first_name"
            $hasError={!!errors.firstName}
            {...register("firstName", { required: "Campo obbligatorio" })}
          />
          {errors.firstName && (
            <ErrorMessage role="alert">{errors.firstName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="surname">Cognome</Label>
          <Input
            id="surname"
            $hasError={!!errors.lastName}
            {...register("lastName", { required: "Campo obbligatorio" })}
          />
          {errors.lastName && (
            <ErrorMessage role="alert">{errors.lastName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="birthday">Data di nascita</Label>
          <Input
            id="birthday"
            type="date"
            min={DateValidation.getMinDateString()}
            max={DateValidation.getTodayString()}
            $hasError={!!errors.birthday}
            {...register("birthday", DateValidation.getMemberValidationRules())}
          />
          {errors.birthday && (
            <ErrorMessage role="alert">{errors.birthday.message}</ErrorMessage>
          )}
        </FormGroup>

        <ButtonGroup>
          <Button
            fullWidth
            type="submit"
            variant="primary"
            loading={isSaving}
            loadingText="SALVATAGGIO IN CORSO..."
          >
            {isNewMember ? "SALVA" : "SALVA MODIFICHE"}
          </Button>
          <Button
            fullWidth
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSaving || isDeleting}
          >
            ANNULLA
          </Button>
          {!isNewMember &&
            !member?.is_owner &&
            member?.subscriptions?.length === 0 && (
              <Button
                fullWidth
                type="button"
                variant="danger"
                onClick={onDelete}
                loading={isDeleting}
                loadingText="ELIMINAZIONE IN CORSO..."
              >
                ELIMINA
              </Button>
            )}
        </ButtonGroup>
      </form>
    </FormContainer>
  );
};
